import { FiInfo } from "react-icons/fi"

import { Title, Container, Number, Checkbox } from "components/Form"
import useT from "utils/hooks/useT"

// SystemSuitability — edits the System Suitability Test specification of an Analysis definition
// (PRD-005 Area 3 / SPEC-017): whether the analysis REQUIRES an SST before results can be sent to
// review, and the SST acceptance criteria (RSD-area ≤, RSD-RT ≤, tailing interval, plate count N ≥,
// resolution Rs ≥, and the expected injection count). Shown only when the LIMS module is on,
// mirroring the Review-Specification section.
//
// It is a controlled component: the parent (AnalysisConfigurationPage) owns the `spec` object and
// persists it via the limsSstSpec slice on save (keyed by the analysis-definition id). The criteria
// fields are always editable; an unset (blank) criterion simply is not judged for that parameter.
//
// Layout (PRD-006 K20): the section heading lives INSIDE the box; the "requires SST" checkbox sits
// side by side with its label at a larger size; the grey criterion labels share ONE fixed column
// width so every row aligns; the explanation comes last via the shared info-icon convention. The
// shared `.LimsConfigBox-*` classes (defined in index.scss) are reused from ReviewSpecification —
// the single SCSS convention for the LIMS analysis-definition config boxes.
const LABEL_WIDTH = "16rem"

export default function SystemSuitability({ spec, onChange }) {
  const { t } = useT("common")
  const set = (field, value) => onChange({ ...spec, [field]: value })

  return (
    <div className="container-compartment AnalysisConfigurationPage-lims-compartment LimsConfigBox">
      <h4 className="LimsConfigBox-heading">{t("limsSstSection")}</h4>

      <div className="LimsConfigBox-fields">
        <label className="LimsConfigBox-checkbox">
          <Checkbox checked={!!spec.requiresSst} onChange={(v) => set("requiresSst", v)} />
          <span className="LimsConfigBox-checkbox-label">{t("limsSstRequires")}</span>
        </label>

        <Container>
          <Title width={LABEL_WIDTH}>{t("limsSstRsdAreaMax")}</Title>
          <Number min={0} value={spec.rsdAreaMax} onChange={(v) => set("rsdAreaMax", v)} />
        </Container>
        <Container>
          <Title width={LABEL_WIDTH}>{t("limsSstRsdRtMax")}</Title>
          <Number min={0} value={spec.rsdRtMax} onChange={(v) => set("rsdRtMax", v)} />
        </Container>
        <Container>
          <Title width={LABEL_WIDTH}>{t("limsSstTailingMin")}</Title>
          <Number min={0} value={spec.tailingMin} onChange={(v) => set("tailingMin", v)} />
        </Container>
        <Container>
          <Title width={LABEL_WIDTH}>{t("limsSstTailingMax")}</Title>
          <Number min={0} value={spec.tailingMax} onChange={(v) => set("tailingMax", v)} />
        </Container>
        <Container>
          <Title width={LABEL_WIDTH}>{t("limsSstPlateCountMin")}</Title>
          <Number min={0} value={spec.plateCountMin} onChange={(v) => set("plateCountMin", v)} />
        </Container>
        <Container>
          <Title width={LABEL_WIDTH}>{t("limsSstResolutionMin")}</Title>
          <Number min={0} value={spec.resolutionMin} onChange={(v) => set("resolutionMin", v)} />
        </Container>
        <Container>
          <Title width={LABEL_WIDTH}>{t("limsSstInjectionCount")}</Title>
          <Number min={1} value={spec.injectionCount} onChange={(v) => set("injectionCount", v)} />
        </Container>
      </div>

      <p className="LimsConfigBox-help">
        <FiInfo className="LimsConfigBox-help-icon" title={t("limsSstRequiresHelp")} />
        <span>{t("limsSstRequiresHelp")}</span>
      </p>
    </div>
  )
}
